import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2Icon, MailIcon, PhoneIcon, XIcon } from 'lucide-react';
import { BookingAgreements } from './BookingAgreements';
import { useLanguage } from '../contexts/LanguageContext';
import { rides } from '../data/rides';
import { stable } from '../data/stable';
import { simplyBookUrl } from '../utils/simplybook';
interface BookingModalProps {
  open: boolean;
  rideId: string | null;
  onClose: () => void;
}
export function BookingModal({
  open,
  rideId,
  onClose
}: BookingModalProps) {
  const {
    t,
    l,
    lang
  } = useLanguage();
  const b = t.booking;
  const ride = rideId ? rides.find(r => r.id === rideId) ?? null : null;
  const url = simplyBookUrl(lang, ride?.simplyBookServiceId);
  const [loaded, setLoaded] = useState(false);
  const [accepted, setAccepted] = useState(false);
  useEffect(() => setLoaded(false), [url, open]);
  useEffect(() => {
    if (open) setAccepted(false);
  }, [open]);
  const showCalendar = accepted && Boolean(url);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  return <AnimatePresence data-id="element-17" data-mp="1wcw28pu4xtrm">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-bark/70 sm:items-center sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} onClick={onClose} data-id="element-18" data-mp="1wdxm9jt4pqlh">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="booking-modal-title" initial={{
        opacity: 0,
        y: 16,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 16,
        scale: 0.98
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={e => e.stopPropagation()} className={`flex w-full max-w-3xl flex-col overflow-hidden rounded-t-xl bg-cream sm:rounded-xl ${showCalendar ? 'h-[92vh] sm:h-[85vh]' : 'max-h-[92vh] overflow-y-auto'}`} data-id="element-19" data-mp="18omjjws7xm4p">
            <header className="flex items-center justify-between gap-4 border-b border-line px-5 py-4" data-id="element-20" data-mp="1dg5ckg9oa973">
              <div data-id="element-21" data-mp="18ic6dn8ddf6t">
                <h2 id="booking-modal-title" className="font-serif text-xl font-bold text-bark" data-id="element-22" data-mp="18fq0ds14n7qp">
                  {ride ? l(ride.title) : b.modalTitle}
                </h2>
                {ride && <p className="text-sm text-bark/65" data-id="element-23" data-mp="ylqy8t11znyr">
                    {l(ride.level)} · {ride.durationMin} {t.rides.min}
                  </p>}
              </div>
              <button type="button" autoFocus onClick={onClose} aria-label={b.close} className="rounded-full p-2 text-bark/70 transition-colors duration-150 hover:bg-linen hover:text-bark focus:outline-none focus-visible:ring-2 focus-visible:ring-hay" data-id="element-24" data-mp="3jbq672i3lx1d">
                <XIcon className="h-5 w-5" data-id="element-25" data-mp="2dgqy7aovhoz9" />
              </button>
            </header>

            {!accepted ? <BookingAgreements onConfirm={() => setAccepted(true)} data-id="element-26" data-mp="2ieis2a92vl63" /> : url ? <div className="relative flex-1 bg-cream" data-id="element-27" data-mp="2mg4aeb4dsrno">
                {!loaded && <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-bark/70" data-id="element-28" data-mp="2rfz0wdk8vf87">
                    <Loader2Icon className="h-8 w-8 animate-spin text-saddle" aria-hidden="true" data-id="element-29" data-mp="2we0j6w9ujaim" />
                    <p data-id="element-30" data-mp="x3iwlib0eb5z">{b.loading}</p>
                  </div>}
                <iframe key={url} src={url} title={b.modalTitle} onLoad={() => setLoaded(true)} className="h-full w-full border-0" allow="payment" data-id="element-31" data-mp="120irqy6bltib" />
              </div> : <div className="px-6 py-10 text-center sm:px-10" data-id="element-32" data-mp="31i2p4u5g9rff">
                <h3 className="font-serif text-2xl font-bold text-bark" data-id="element-33" data-mp="2xhiqfm58afr6">{b.soonTitle}</h3>
                <p className="mx-auto mt-2 max-w-md leading-relaxed text-bark/75" data-id="element-34" data-mp="2xfo3k2aecdkg">{b.soonText}</p>
                <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row" data-id="element-35" data-mp="29pk6yevmle2y">
                  <a href={`tel:${stable.phone.replace(/\s/g, '')}`} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-saddle px-6 py-3 font-bold text-cream transition-colors duration-150 hover:bg-leather" data-id="element-36" data-mp="24qqr4fsv9azm">
                    <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-37" data-mp="1q1ktuxdl9uud" />
                    {b.call} {stable.phone}
                  </a>
                  <a href={`mailto:${stable.email}${ride ? `?subject=${encodeURIComponent(l(ride.title))}` : ''}`} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-saddle px-6 py-3 font-bold text-saddle transition-colors duration-150 hover:bg-saddle hover:text-cream" data-id="element-38" data-mp="1by5amnu10p18">
                    <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-39" data-mp="1up6arf6vuqmd" />
                    {b.write}
                  </a>
                </div>
              </div>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}